import React from 'react'

import {
  fetchTrackMetadata,
  initEditedTrack,
} from '@disco/state-management/actions/ServerActions'
import { getTrack } from '@disco/state-management/selectors/tracks'
import {
  getCommonBasicMetadataFields,
  getCommonExtraMetadataFields,
} from '@disco/utils/commonMetadata'

import type { EditableTrack, Track, TrackId } from 'types/track'

import EditMetadataModal from 'components/modals/track/edit-metadata'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  trackId: TrackId | null
  onClose: () => void
}

/**
 * Opens the same track metadata modal used by the explorer's edit-metadata
 * route, driven by local state instead of a URL so the caller's page/URL
 * is preserved once the modal closes.
 */
export const TrackMetadataModal = ({ trackId, onClose }: Props) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = React.useState(false)
  const track: Track | null | undefined = useSelector(state =>
    trackId ? getTrack(state, { id: trackId }) : null,
  )

  React.useEffect(() => {
    if (!trackId) return

    const loadTrack = async () => {
      setIsLoading(true)
      await dispatch(fetchTrackMetadata(trackId))
      setIsLoading(false)
    }

    loadTrack()
  }, [trackId, dispatch])

  React.useEffect(() => {
    if (!track) return

    const editedTrack: EditableTrack = {
      id: track.id,
      url: track.url,
      metadata: {
        ...getCommonBasicMetadataFields([
          {
            artwork: track.artwork_sizes?.full || track.artwork,
            name: track.name,
            album: track.album,
            artist: track.artist,
          },
        ]),
        ...getCommonExtraMetadataFields([track.metadata]),
      },
      comments: track.comments ?? [],
      track_writers: track.track_writers,
      groups: track.groups,
    }
    dispatch(initEditedTrack(editedTrack))
  }, [track, dispatch])

  if (!trackId || !track) {
    return null
  }

  return (
    <EditMetadataModal
      editedTrackRefreshing={isLoading}
      track={track}
      onClose={onClose}
    />
  )
}
